/**
 * Environment Detection Utility
 * 
 * Centralized detection and URL construction for different deployment environments:
 * - e2b.app: Internal AI agent development tool with dynamic instance IDs
 * - Production: Azure Static Web Apps with fixed URLs
 * - Local: Development on localhost
 */

const E2B_REGEX = /(\d+)-(.+)\.e2b\.app/;

/**
 * Environment Information
 */
export interface EnvironmentInfo {
  isE2B: boolean;
  isLocal: boolean;
  isProduction: boolean;
  instanceId: string | null;
  hostname: string;
}

/**
 * Detect Current Environment
 * 
 * @returns Environment information object
 */
export function detectEnvironment(): EnvironmentInfo {
  if (typeof window === 'undefined') {
    return {
      isE2B: false,
      isLocal: false,
      isProduction: false,
      instanceId: null,
      hostname: '',
    };
  }

  const hostname = window.location.hostname;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  const isE2B = hostname.includes('.e2b.app');
  
  let instanceId: string | null = null;
  if (isE2B) {
    const match = hostname.match(E2B_REGEX);
    if (match) {
      instanceId = match[2]; // Extract instance ID
    }
  }

  return {
    isE2B,
    isLocal,
    isProduction: !isE2B && !isLocal,
    instanceId,
    hostname,
  };
}

/**
 * Get Backend URL based on current environment
 * 
 * @param port - Backend port (default: 5000 for .NET)
 * @returns Backend URL
 */
export function getBackendUrl(port: number = 5000): string {
  const env = detectEnvironment();

  if (env.isE2B && env.instanceId) {
    return `https://${port}-${env.instanceId}.e2b.app`;
  }

  if (env.isLocal) {
    return `http://localhost:${port}`;
  }

  // Production - use environment variable
  return import.meta.env.VITE_API_URL || `http://localhost:${port}`;
}

/**
 * Get Frontend URL based on current environment
 * 
 * @param port - Frontend port (default: 3000)
 * @returns Frontend URL (useful for constructing webhook URLs, etc.)
 */
export function getFrontendUrl(port: number = 3000): string {
  const env = detectEnvironment();

  if (env.isE2B && env.instanceId) {
    return `https://${port}-${env.instanceId}.e2b.app`;
  }

  if (env.isLocal) {
    return `http://localhost:${port}`;
  }

  // Production - use current origin
  return window.location.origin;
}

/**
 * Get MCP Server URL for Claude Desktop Integration
 * 
 * @param workflowId - Workflow ID for MCP server
 * @param port - Backend port (default: 5000 for .NET)
 * @returns MCP server URL
 */
export function getMcpServerUrl(workflowId: string, port: number = 5000): string {
  const backendUrl = getBackendUrl(port);
  return `${backendUrl}/mcp/${workflowId}`;
}

/**
 * Get Webhook URL for workflow triggers
 * 
 * @param workflowId - Workflow ID
 * @param nodeId - Node ID for webhook
 * @param port - Backend port (default: 5000 for .NET)
 * @returns Webhook URL
 */
export function getWebhookUrl(workflowId: string, nodeId: string, port: number = 5000): string {
  const backendUrl = getBackendUrl(port);
  return `${backendUrl}/api/webhooks/${workflowId}/${nodeId}`;
}

/**
 * Get Chat URL (for embedded or hosted chat)
 * 
 * @param workflowId - Workflow ID
 * @param mode - 'embed' or 'hosted'
 * @param port - Frontend port (default: 3000)
 * @returns Chat page URL
 */
export function getChatUrl(workflowId: string, mode: 'embed' | 'hosted' = 'hosted', port: number = 3000): string {
  const frontendUrl = getFrontendUrl(port);
  const path = mode === 'embed' ? 'embed' : 'chat';
  return `${frontendUrl}/${path}/${workflowId}`;
}

/**
 * Check if running in e2b.app environment
 * 
 * @returns true if in e2b.app
 */
export function isE2BEnvironment(): boolean {
  return detectEnvironment().isE2B;
}

/**
 * Get e2b.app instance ID
 * 
 * @returns Instance ID or null if not in e2b.app
 */
export function getE2BInstanceId(): string | null {
  return detectEnvironment().instanceId;
}
